import { useMemo, useState } from "react";
import { useApp, useUi, useUiDispatch } from "@/app/AppContext";
import { useSelectedView, useVisibilityFilter } from "@/app/selectors";
import { softDeleteIntervention } from "@/data/interventionsRepository";
import {
  buildDynamicColumns,
  compareListItems,
  getHighlightedSections,
  localized,
  type ListSortKey,
} from "@/domain/interventionList";
import {
  canCreateIntervention,
  canDeleteIntervention,
  canEditIntervention,
  canSubmitOwn,
  isRespondentOnly,
  isSuperadmin,
} from "@/domain/permissions";
import type { Intervention } from "@/domain/types";
import { InterventionDetails } from "@/features/details/InterventionDetails";
import { useI18n } from "@/i18n/useI18n";
import { RespondentIntro } from "./RespondentIntro";

const DESCRIPTION_PREVIEW_LENGTH = 150;

export function ListTab() {
  const i18n = useI18n();
  const { t, language } = i18n;
  const { authMode, user, data } = useApp();
  const ui = useUi();
  const dispatch = useUiDispatch();
  const view = useSelectedView();
  const isVisible = useVisibilityFilter({ includeSearch: true });
  const isRespondentList = authMode === "individual" && isRespondentOnly(user);
  const showWorkflowColumns =
    authMode === "individual" && !data.localWorkbook && canCreateIntervention(user);
  const { key: sortKey, direction } = ui.listSort;

  const colorLabel = (item: Intervention) => {
    const code = item.codes[view.id] ?? "";
    return view.values[code] ? i18n.viewValueLabel(view, code) : t("unclassifiedItems");
  };

  const dynamicColumns = useMemo(
    () => buildDynamicColumns(data.dataset.items, language),
    [data.dataset.items, language],
  );
  const validKeys = new Set<string>([
    "number",
    "name",
    "location",
    "color",
    "links",
    "description",
    ...dynamicColumns.map((c) => c.key),
  ]);
  const effectiveSort: ListSortKey = validKeys.has(sortKey) ? sortKey : "number";

  // Respondents have no filters and must see every row RLS returns for them.
  const rows = data.dataset.items
    .filter((item) => isRespondentList || isVisible(item))
    .sort(
      compareListItems(effectiveSort, validKeys.has(sortKey) ? direction : "asc", {
        language,
        colorLabel,
      }),
    );

  const { panel } = ui;
  if (isRespondentList && panel.kind === "details") {
    const item = data.dataset.items.find((entry) => entry.id === panel.itemId);
    if (item) {
      return (
        <div id="intervention-list">
          <div id="respondent-details-box" className="info-box">
            <InterventionDetails
              item={item}
              embedded
              onClose={() => dispatch({ type: "closePanel" })}
            />
          </div>
        </div>
      );
    }
  }

  const openDetails = (item: Intervention) => {
    dispatch({ type: "showDetails", itemId: item.id });
    if (!isRespondentList) dispatch({ type: "setTab", tab: "matrix" });
  };
  const openEditor = (item: Intervention) => {
    dispatch({ type: "setTab", tab: "matrix" });
    dispatch({ type: "editIntervention", itemId: item.id, editOnly: true });
  };

  const header = (key: ListSortKey, label: string, title?: string) => (
    <th
      key={key}
      className={`sortable${effectiveSort === key ? " sorted" : ""}`}
      title={title}
      onClick={() => dispatch({ type: "sortList", key })}
    >
      {label}
      {effectiveSort === key && (
        <span className="list-sort-arrow">{direction === "asc" ? " ▲" : " ▼"}</span>
      )}
    </th>
  );
  const columnCount =
    5 +
    (showWorkflowColumns ? 2 : 0) +
    (ui.listDetailsLevel === 1 ? dynamicColumns.length : 0) +
    (ui.listDetailsLevel === 2 ? 1 : 0);

  return (
    <div id="intervention-list">
      <div id="intervention-list-content">
        <h3 id="list-title">{t("listTitle")}</h3>
        {/* Respondents get the button in their intro instead. */}
        {authMode === "individual" && canSubmitOwn(user) && <RespondentIntro />}
        {authMode === "individual" &&
          !data.localWorkbook &&
          canCreateIntervention(user) &&
          !canSubmitOwn(user) && (
            <button
              type="button"
              className="new-intervention-btn"
              onClick={() => dispatch({ type: "createIntervention" })}
            >
              {t("newInterventionButton")}
            </button>
          )}
        <div className="intervention-list-table-wrap">
          <table className="intervention-list-table">
            <thead>
              <tr>
                {header("number", t("listNumber"))}
                {header("name", t("listName"))}
                {header("location", t("listLocation"))}
                {header("color", t("listColor"))}
                {ui.listDetailsLevel === 1 &&
                  dynamicColumns.map((column) => header(column.key, column.label, column.title))}
                {ui.listDetailsLevel === 2 && header("description", t("listDescription"))}
                {header("links", t("listLinks"))}
                {showWorkflowColumns && <th>{t("listStatus")}</th>}
                {showWorkflowColumns && <th>{t("listActions")}</th>}
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr>
                  <td colSpan={columnCount} className="intervention-list-empty">
                    {isRespondentList ? t("respondentNoSubmissions") : t("listEmpty")}
                  </td>
                </tr>
              )}
              {rows.map((item) => (
                <ListRow
                  key={item.id}
                  item={item}
                  colorLabel={colorLabel(item)}
                  color={view.values[item.codes[view.id] ?? ""]?.color ?? "#ffffff"}
                  dynamicColumns={
                    ui.listDetailsLevel === 1 ? dynamicColumns.map((column) => column.title) : []
                  }
                  showDescription={ui.listDetailsLevel === 2}
                  showWorkflow={showWorkflowColumns}
                  canEdit={canEditIntervention(user, item)}
                  canDelete={authMode === "individual" && canDeleteIntervention(user, item)}
                  onOpen={() => openDetails(item)}
                  onEdit={() => openEditor(item)}
                  onDelete={async () => {
                    await softDeleteIntervention(item.dbId!, isSuperadmin(user));
                    if (!(await data.reload())) throw new Error(t("editRefreshError"));
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface ListRowProps {
  item: Intervention;
  colorLabel: string;
  color: string;
  dynamicColumns: string[];
  showDescription: boolean;
  showWorkflow: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onOpen(): void;
  onEdit(): void;
  onDelete(): Promise<void>;
}

function ListRow(props: ListRowProps) {
  const { t, language } = useI18n();
  const { item } = props;
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const description = localized(item.description, language);
  const highlighted = getHighlightedSections(item, language);
  const status = item.status === "draft" || item.status === "submitted" ? item.status : "approved";
  const statusLabel = {
    draft: t("statusDraft"),
    submitted: t("statusSubmitted"),
    approved: t("statusApproved"),
  }[status];

  const remove = async () => {
    if (!window.confirm(t("deleteConfirm"))) return;
    setDeleting(true);
    try {
      await props.onDelete();
    } catch (error) {
      console.error("Could not delete intervention:", error);
      window.alert(`${t("editDeleteError")} (${(error as Error).message})`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <tr>
      <td>{item.itemNumber}</td>
      <td>
        <button
          type="button"
          className="intervention-list-title-button"
          title={description || undefined}
          onClick={props.onOpen}
        >
          {localized(item.display, language)}
        </button>
      </td>
      <td>{item.region}</td>
      <td>
        <span className="intervention-list-color">
          <span className="intervention-list-swatch" style={{ backgroundColor: props.color }} />
          <span>{props.colorLabel}</span>
        </span>
      </td>
      {props.dynamicColumns.map((title) => (
        <td
          key={title}
          className="intervention-list-data-cell"
          title={highlighted[title] || undefined}
        >
          {highlighted[title] ?? ""}
        </td>
      ))}
      {props.showDescription &&
        (description.length > DESCRIPTION_PREVIEW_LENGTH ? (
          <td
            className={`intervention-list-data-cell intervention-list-description-cell intervention-list-description-expandable${expanded ? " intervention-list-description-expanded" : ""}`}
            title={description}
            onClick={() => setExpanded((value) => !value)}
          >
            <span className="intervention-list-description-text">
              {expanded
                ? description
                : `${description.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()}…`}
            </span>
          </td>
        ) : (
          <td
            className="intervention-list-data-cell intervention-list-description-cell"
            title={description || undefined}
          >
            {description}
          </td>
        ))}
      <td className="intervention-list-links-cell">
        {item.links.length > 0 && (
          <span className="intervention-list-links-wrap">
            {item.links.slice(0, 3).map((url, index) => (
              <a
                key={`${index}:${url}`}
                className="intervention-list-link-icon"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={url}
              >
                🔗
              </a>
            ))}
            {item.links.length > 3 && (
              <span
                className="intervention-list-link-plus"
                title={`+${item.links.length - 3} ${t("listLinks").toLowerCase()}`}
              >
                +
              </span>
            )}
          </span>
        )}
      </td>
      {props.showWorkflow && (
        <td className="intervention-list-status-cell">
          <span className={`status-badge ${status}`}>{statusLabel}</span>
        </td>
      )}
      {props.showWorkflow && (
        <td className="intervention-list-edit-cell">
          <div className="intervention-list-actions">
            {props.canEdit && (
              <button type="button" className="intervention-list-edit-btn" onClick={props.onEdit}>
                {t("listEdit")}
              </button>
            )}
            {props.canDelete && (
              <button
                type="button"
                className="intervention-list-delete-btn"
                disabled={deleting}
                onClick={() => void remove()}
              >
                {t("deleteButton")}
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}
